import type { Offer, SalonProfile } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { CalendarHeart, Menu, Phone, Sparkles } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'
import { Logo } from '@/components/site/logo'
import { WhatsAppIcon } from '@/components/site/brand-icons'
import { cms } from '@/lib/cms'
import { telLink, whatsappLink } from '@/lib/phone'
import { bookingWhatsAppText } from '@/lib/salon'
import { cn } from '@/lib/utils'
import { PUBLIC_NAV } from '@/constants/site'

interface SiteHeaderProps {
    profile: SalonProfile
    announcement: Offer | null
}

export const SiteHeader = ({ profile, announcement }: SiteHeaderProps) => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <>
            {announcement && (
                <div className='bg-navy-950 text-cream-100'>
                    <div className='mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2 text-center text-xs sm:text-[0.8125rem]'>
                        <Sparkles className='hidden size-4 shrink-0 text-gold-300 sm:block' />
                        <p className='leading-snug'>
                            <span data-mdlfy-cms={cms('offers', announcement.id, 'title')} className='font-semibold text-gold-200'>
                                {announcement.title}
                            </span>
                            <span aria-hidden='true' className='mx-2 text-gold-400'>|</span>
                            <span data-mdlfy-cms={cms('offers', announcement.id, 'discount_label')}>
                                {announcement.discount_label}
                            </span>
                            {' '}with code{' '}
                            <span className='rounded-sm bg-cream-50/10 px-1.5 py-0.5 font-semibold tracking-wider text-gold-200'>
                                {announcement.code}
                            </span>
                        </p>
                        <Link href={`/book?offer=${encodeURIComponent(announcement.code ?? '')}`} className='ml-1 hidden shrink-0 font-semibold text-gold-300 underline-offset-4 hover:underline sm:inline'>
                            Book now
                        </Link>
                    </div>
                </div>
            )}

            <header
                className={cn(
                    'sticky top-0 z-40 border-b transition-all duration-300',
                    scrolled ? 'border-border/80 bg-background/90 shadow-[0_8px_30px_-18px_rgba(19,35,63,0.35)] backdrop-blur-md' : 'border-transparent bg-background'
                )}
            >
                <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-20 lg:px-8'>
                    <Logo profileId={profile.id} shortName={profile.short_name} />

                    <nav aria-label='Main' className='hidden items-center gap-1 lg:flex'>
                        {PUBLIC_NAV.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={isActive(item.href) ? 'page' : undefined}
                                className={cn(
                                    'relative rounded-full px-4 py-2 text-sm font-medium transition-colors hover:text-navy-900',
                                    isActive(item.href) ? 'text-navy-900' : 'text-navy-900/70'
                                )}
                            >
                                {item.label}
                                {isActive(item.href) && <span aria-hidden='true' className='absolute inset-x-4 -bottom-0.5 h-px bg-gold-500' />}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex items-center gap-2'>
                        {profile.phone && (
                            <a
                                href={telLink(profile.phone)}
                                className='hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-navy-900/80 transition hover:text-navy-900 xl:flex'
                            >
                                <Phone className='size-4 text-gold-600' />
                                <span data-mdlfy-cms={cms('salon_profile', profile.id, 'phone')}>
                                    {profile.phone}
                                </span>
                            </a>
                        )}

                        <Link href='/book' className={cn(buttonVariants({ variant: 'gold' }), 'hidden sm:inline-flex')}>
                            <CalendarHeart />
                            Book appointment
                        </Link>

                        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                            <SheetTrigger
                                className='inline-flex size-11 items-center justify-center rounded-full border border-border bg-card text-navy-900 transition hover:border-gold-400 lg:hidden'
                                aria-label='Open menu'
                            >
                                <Menu className='size-5' />
                            </SheetTrigger>
                            <SheetContent side='right' className='gap-0 bg-background p-0 data-[side=right]:w-[86%]'>
                                <div className='border-b border-border px-6 pt-6 pb-5'>
                                    <SheetTitle className='sr-only'>Menu</SheetTitle>
                                    <SheetDescription className='sr-only'>Site navigation and booking</SheetDescription>
                                    <Logo profileId={profile.id} shortName={profile.short_name} />
                                </div>

                                <nav aria-label='Mobile' className='flex flex-col px-3 py-4'>
                                    {PUBLIC_NAV.map(item => (
                                        <SheetClose
                                            key={item.href}
                                            render={(
                                                <Link
                                                    href={item.href}
                                                    className={cn(
                                                        'rounded-xl px-4 py-3.5 font-heading text-2xl font-semibold transition-colors hover:bg-muted',
                                                        isActive(item.href) ? 'text-emerald-700' : 'text-navy-900'
                                                    )}
                                                />
                                            )}
                                            nativeButton={false}
                                        >
                                            {item.label}
                                        </SheetClose>
                                    ))}
                                </nav>

                                <div className='mt-auto space-y-3 border-t border-border p-6'>
                                    <Link href='/book' className={cn(buttonVariants({ variant: 'gold', size: 'lg' }), 'w-full')}>
                                        <CalendarHeart />
                                        Book appointment
                                    </Link>
                                    {profile.whatsapp && (
                                        <a
                                            href={whatsappLink(profile.whatsapp, bookingWhatsAppText(profile))}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'w-full')}
                                        >
                                            <WhatsAppIcon className='size-4 text-emerald-600' />
                                            Chat on WhatsApp
                                        </a>
                                    )}
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>
        </>
    )
}
